import type { FieldValues } from "react-hook-form";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { SkeletonFieldRenderer } from "./skeleton-field-renderer";
import type { FormSchema, FormSectionConfig } from "./types";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";

// Tailwind requires full class names (no dynamic interpolation)
const gridColsClasses: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

const colSpanClasses: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
};

interface SchemaFormSkeletonProps<T extends FieldValues> {
  schema: FormSchema<T>;
}

function FormSectionSkeleton<T extends FieldValues>({ section }: { section: FormSectionConfig<T> }) {
  return (
    <div className={cn("space-y-4", section.className)}>
      {section.title && <Skeleton className="h-6 w-32" />}
      {section.fields.map(field => (
        <SkeletonFieldRenderer key={field.name} field={field} />
      ))}
    </div>
  );
}

export function SchemaFormSkeleton<T extends FieldValues>({ schema }: SchemaFormSkeletonProps<T>) {
  const columns = schema.layout?.columns ?? 2;

  return (
    <div className={cn("grid w-full gap-4 grid-cols-1", gridColsClasses[columns])}>
      {schema.sections.map(section => (
        <FormSectionSkeleton key={section.id} section={section} />
      ))}

      {/* Footer skeleton */}
      <div className={cn("col-span-1 mt-4 flex justify-end gap-2", colSpanClasses[columns])}>
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
  );
}
